"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";

import currentPatientIdSelector from "@/redux/slice/auth/selectors/currentPatientId";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import currentAddressIdSelector from "@/redux/slice/location/selectors/currentAddressId";
import pharmaAddressDetailsSelector from "@/redux/slice/location/selectors/pharmaAddressDetails";
import { cartItemsSelector } from "@/redux/slice/cart/selectors/cartItems";
import { useAppSelector } from "@/redux/hooks";

import { setIsCartUpdated, setUserActionPayload } from "@/redux/slice/cart/slice";
import { CartItem } from "@/redux/slice/cart/types/CartItem";
import signedInStatusSelector from "@/redux/slice/auth/selectors/signedInState";
import { PharmaAddressDetails } from "@/redux/slice/location/interfaces/PharmaAddressDetails";
import userActionPayloadSelector from "@/redux/slice/cart/selectors/userActionPayload";

import { fetchServerCart } from "@/redux/slice/cart/utils/fetchServerCart";
import { syncCart } from "@/redux/slice/cart/utils/syncCart";
import { saveCartV2, saveCartV2Variables } from "@/components/graphql/types/saveCartV2";
import { SAVE_CART_V2 } from "@/components/graphql/serverCartV2";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import { CartInputDataV2, GuestCartInputDataV2 } from "@/components/graphql/types/globalTypes";
import { saveServerCart } from "@/redux/slice/cart/utils/saveServerCart";
import useMobileDetect from "@/libs/useMobileDetect";
import guestUserActionPayloadSelector from "../selectors/guestUserActionPayload";

declare global {
  interface Window {
    ReactNativeWebView: any;
  }
}

const useManageServerCart = ({ dispatch }) => {
  // router and network hooks
  const apolloClient = useApolloClient();
  const currentDevice = useMobileDetect();
  const isWebView = currentDevice.isWebView();
  const routerPathName = usePathname() as string;

  const [isCartFetchingUpdatingRequired, setIsCartFetchingUpdatingRequired] = useState(true);

  // selectors
  const currentPatientId = useAppSelector(currentPatientIdSelector);
  const currentPatient = useAppSelector(currentPatientSelector);
  const isSignedIn = useAppSelector(signedInStatusSelector);
  const userActionPayload = useAppSelector(userActionPayloadSelector);
  const cartItems = useAppSelector(cartItemsSelector);
  const pharmaAddressDetails = useAppSelector(pharmaAddressDetailsSelector);
  const deliveryAddressId = useAppSelector(currentAddressIdSelector);
  const isCartUpdated = useAppSelector((state) => state?.cart?.isCartUpdated);
  const guestUserActionPayload = useAppSelector(guestUserActionPayloadSelector);

  const [saveCartMutation] = useMutation<saveCartV2, saveCartV2Variables>(SAVE_CART_V2);

  const { id: patientId = "" } = currentPatient || {};

  useEffect(() => {
    if (pharmaAddressDetails?.pincode) {
      localStorage.setItem("pharmaPincode", pharmaAddressDetails.pincode);
    }
  }, [pharmaAddressDetails]);

  useEffect(() => {
    localStorage.setItem("deliveryAddressId", deliveryAddressId);
  }, [deliveryAddressId]);

  useEffect(() => {
    if (isSignedIn === SignedinState.SIGNEDOUT) syncCart(dispatch);
    // this is for transition from signed out to signed in

    if (isSignedIn === SignedinState.SIGNEDIN) {

      const pharmaAddressDetailsFromLocalStorage: PharmaAddressDetails =
        localStorage.getItem("pharmaAddressDetails") && JSON.parse(localStorage.getItem("pharmaAddressDetails"));
      localStorage.removeItem("pharmaAddressDetails");
      // create cart payload
      const cartInputPayload: Partial<CartInputDataV2> = {
        ...(!(isWebView && window?.ReactNativeWebView) &&
          pharmaAddressDetailsFromLocalStorage && {
            address: {
              zipcode: pharmaAddressDetailsFromLocalStorage?.pincode,
              ...(pharmaAddressDetailsFromLocalStorage?.lat &&
                pharmaAddressDetailsFromLocalStorage?.lng && {
                  coordinate: {
                    latitude: Number(pharmaAddressDetailsFromLocalStorage?.lat),
                    longitude: Number(pharmaAddressDetailsFromLocalStorage?.lng),
                  },
                }),
            },
          }),
      };

      if (Object.keys(cartInputPayload)?.length) {
        dispatch(setUserActionPayload(cartInputPayload));
      } else {
        setIsCartFetchingUpdatingRequired(false);
        fetchServerCart(
          {
            patientId,
            apolloClient,
          },
          dispatch
        );
      }
    }
  }, [isSignedIn, routerPathName]);

  useEffect(() => {
    if (isSignedIn === SignedinState.SIGNEDIN && userActionPayload) {
      const cartInputDataV2: CartInputDataV2 = {
        ...userActionPayload,
        patientId: currentPatientId,
      };
      saveServerCart({
        cartInputDataV2,
        dispatch,
        saveCartMutation,
        setIsCartFetchingUpdatingRequired,
        isCartFetchingUpdatingRequired,
      });
    }
  }, [userActionPayload]);
  useEffect(() => {
    // Save cart for logged out user
    if (isSignedIn === SignedinState.SIGNEDOUT && guestUserActionPayload) {
      const guestCartInputDataV2: GuestCartInputDataV2 = {
        ...guestUserActionPayload,
        id: (localStorage?.getItem("guestCartId") as string) || "",
      };
      saveServerCart({
        guestCartInputDataV2,
        dispatch,
        setIsCartFetchingUpdatingRequired,
        isCartFetchingUpdatingRequired,
      });
    }
  }, [guestUserActionPayload]);
};

export default useManageServerCart;
